import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Shared "back" behaviour for the driver and contractor profile pages.
 *
 * These pages have no route param — a profile is opened by seeding Redux and
 * switching an internal `currentView`. That means the same URL (`/profiles/drivers`)
 * can be reached three ways:
 *   1. Clicking a row inside the list (no navigation, just a Redux view change).
 *   2. Deep-linked in from another page (accounting statement detail, truck P&L, …),
 *      which passes a `returnTo` path in `location.state`.
 *   3. Direct URL access / refresh (no history, no state).
 *
 * The back button should return the user to wherever they came from. When a
 * `returnTo` is present we honour it (case 2); otherwise we fall back to the
 * profile list via `onBackToList` (cases 1 and 3).
 */
export interface ProfileBackNavState {
    /** Absolute path to return to when the back button is pressed. */
    returnTo?: string
    /**
     * The origin page's own `location.state`, replayed when we navigate back.
     *
     * Some origin pages seed state that lives only in `location.state` and is not
     * reflected in the URL — Truck P&L detail, for example, initialises its date
     * range from `state.dateRange`. Returning with only `returnTo` would land on
     * the right URL but silently reset that state, so we carry it through.
     */
    returnToState?: unknown
}

/**
 * Builds the `location.state` payload that makes the profile back button return to the given location.
 *
 * Pass the caller's full `location` so any state it was rendered with (not just
 * `pathname`/`search`) survives the round trip — see {@link ProfileBackNavState.returnToState}.
 */
export const buildReturnToState = (location: { pathname: string; search: string; state?: unknown }): ProfileBackNavState => ({
    returnTo: `${location.pathname}${location.search}`,
    returnToState: location.state ?? undefined,
})

export const useProfileBackNavigation = (onBackToList: () => void, onLeave?: () => void) => {
    const location = useLocation()
    const navigate = useNavigate()

    const navState = location.state as ProfileBackNavState | null
    const hasReturnTo = Boolean(navState?.returnTo)

    const handleBack = useCallback(() => {
        const returnTo = navState?.returnTo
        if (returnTo) {
            // Leaving the profiles page entirely — reset the in-page view state first so a
            // later visit (e.g. a sidebar click) opens on the list, not a stale edit wizard
            // for the entity viewed here.
            onLeave?.()
            // Replay the origin's own state so state-only settings (Truck P&L's date range)
            // survive the round trip instead of silently resetting to their defaults.
            navigate(returnTo, navState?.returnToState ? { state: navState.returnToState } : undefined)
            return
        }
        onBackToList()
    }, [navState, navigate, onBackToList, onLeave])

    return { handleBack, hasReturnTo }
}
